import React, {FC} from 'react';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {blendColors} from '../../../../libs/shared/ui/src/styles/colorHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeammateOnlineStatusesEnum} from '../../../store/data/teammates/onlineTeammatesSelectors';
import {TeammateAvatarWithStatus} from '../../common/avatars/teammateAvatarWithStatus';
import {NavigationLineChevron} from '../../common/navigation/navigationLineChevron';
import {NavigationLineIcon} from '../../common/navigation/navigationLineIcon';
import {NavigationLineLabel} from '../../common/navigation/navigationLineLabel';
import {WorkspacesEditorRarelyViewedBadge} from './editor/workspacesEditorRarelyViewedBadge';
import {WorkspaceLineDropTarget, WorkspaceLineDropTargetProps} from './line/workspaceLineDropTarget';
import {WorkspaceLineMoreProps} from './line/workspaceLineMore';
import {WorkspaceLineMoreContainer} from './line/workspaceLineMoreContainer';
import {StyledLabelDiv, StyledTextDiv} from './workspaceCustomViewItem';

/*
 * Props.
 */

export interface WorkspaceTeammateItemProps
  extends WorkspaceLineDropTargetProps,
    Omit<WorkspaceLineMoreProps, 'isGuidedDemoActive'> {
  /** The teammate to display. */
  teammate: TeammateModel;
  /** Whether the teammate is online. */
  onlineStatus: TeammateOnlineStatusesEnum;
  /** Should the teammate name be bold. */
  isUnread?: boolean;
  /** Whether the current item is currently expanded. */
  isExpanded?: boolean;
  /** Handler to collapse/expand this item. */
  onToggleExpandClick?: Handler;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  isRecentlyViewed?: boolean;
}

/*
 * Component.
 */

/** Display a teammate as a P1 workspace line. */
export const WorkspaceTeammateItem: FC<WorkspaceTeammateItemProps> = (props) => {
  const theme = useUserTheme();
  const {teammate, isSelected} = props;

  // Find the colors to use for the avatar borders.
  const backgroundHoverBlend = blendColors(theme.background, theme.itemBackgroundHover);
  const backgroundSelectedBlend = blendColors(theme.background, theme.itemBackgroundSelected);
  const avatarBorderColor = isSelected ? backgroundSelectedBlend : theme.background;
  const avatarBorderHoverColor = isSelected ? undefined : backgroundHoverBlend;
  const itemName = props.children || buildTeammateDisplayName(teammate);

  return (
    <WorkspaceLineDropTarget
      {...props}
      shouldHidePreviewBadge={props.isEditingWorkspace}
      renderTooltip={() => itemName}
      interactionId={`workspace-teammate-${teammate.id}`}
    >
      {maybeRenderChevron(props)}
      <NavigationLineIcon>
        <TeammateAvatarWithStatus
          teammate={teammate}
          onlineStatus={props.onlineStatus}
          size={VisualSizesEnum.SMALL}
          borderColor={avatarBorderColor}
          borderHoverColor={avatarBorderHoverColor}
        />
      </NavigationLineIcon>
      <NavigationLineLabel isUnread={props.isUnread} isEditingWorkspace={props.isEditingWorkspace}>
        <StyledLabelDiv>
          <StyledTextDiv>
            <TooltipOverflowCondition>{itemName}</TooltipOverflowCondition>
          </StyledTextDiv>
          {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
        </StyledLabelDiv>
      </NavigationLineLabel>
      <WorkspaceLineMoreContainer
        renderMenu={props.renderMenu}
        badgeValue={props.badgeValue}
        isUnread={props.isUnread}
        loadingType={props.loadingType}
        shouldShowOfflineIcon={props.shouldShowOfflineIcon}
        isEditingWorkspace={props.isEditingWorkspace}
        isChecked={props.isChecked}
        shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
      />
    </WorkspaceLineDropTarget>
  );
};

/*
 * Helpers.
 */

function maybeRenderChevron(props: WorkspaceTeammateItemProps) {
  const {onToggleExpandClick} = props;
  if (!onToggleExpandClick) {
    return null;
  }

  return <NavigationLineChevron {...props} onToggleExpandClick={onToggleExpandClick} />;
}
